import type { LaunchCodingAssistantArgs } from '@kizenapps/engine';
import { guardKiCodeSurface } from './activeSurface';
import {
  businessAppOpenArgs,
  isValidKiCodeTarget,
  type KiCodeOpenArgs,
} from './profiles';
import type { KiCodeLauncher } from './useKiCode';

/**
 * The open a guard eventually runs. Availability is read when it runs, since
 * access can change while a busy surface's prompt is showing; the check
 * mirrors the reducer's 'open' guard (kiCodeState.ts), which drops the open
 * otherwise. Returns whether the open was dispatched.
 */
export const kiCodeOpenAction =
  (
    { profile, context }: KiCodeOpenArgs,
    isAvailable: () => boolean,
    dispatchOpen: () => void
  ) =>
  (): boolean => {
    if (!isAvailable() || !isValidKiCodeTarget({ profile, context })) {
      return false;
    }
    dispatchOpen();
    return true;
  };

/**
 * Runs `replace` now when `sameTarget`, otherwise behind the active surface's
 * busy guard. Settles once: with `replace`'s result (whether the open was
 * accepted when it ran), or false when the guard's prompt is cancelled,
 * replaced or unmounted.
 */
export const guardedKiCodeOpen = (
  replace: () => boolean,
  sameTarget: boolean
): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    const run = () => resolve(replace());
    if (sameTarget) run();
    else guardKiCodeSurface(run, undefined, () => resolve(false));
  });

/** False for an unsupported profile or unavailable KiCode; otherwise whether the open went through. */
export const launchKiCode = (
  { profile, initialPrompt }: LaunchCodingAssistantArgs,
  { available, open }: KiCodeLauncher
): boolean | Promise<boolean> => {
  if (profile !== 'business-app' || !available) return false;
  return open({ ...businessAppOpenArgs(), initialPrompt });
};
